import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  Check,
  ChevronDown,
  Clipboard,
  CloudUpload,
  Code2,
  Copy,
  Eye,
  Image as ImageIcon,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  MoreHorizontal,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import "./styles.css";

const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/avif",
  "image/x-icon",
  "image/vnd.microsoft.icon",
];
const ACCEPT_ATTRIBUTE = `${ACCEPTED_TYPES.join(",")},.ico`;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

function App() {
  const [session, setSession] = useState("loading");

  useEffect(() => {
    api("/api/session")
      .then((data) => setSession(data.authenticated ? "authenticated" : "anonymous"))
      .catch(() => setSession("anonymous"));
  }, []);

  if (session === "loading") return <AppLoading />;
  if (session === "anonymous") return <Login onSuccess={() => setSession("authenticated")} />;
  return <Library onLogout={() => setSession("anonymous")} />;
}

function AppLoading() {
  return (
    <main className="centered-page" aria-label="正在加载">
      <div className="brand-mark" aria-hidden="true"><ImageIcon size={22} /></div>
      <LoaderCircle className="spin muted" size={22} />
    </main>
  );
}

function Login({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      await api("/api/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      onSuccess();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-panel" aria-labelledby="login-title">
        <div className="brand-row">
          <div className="brand-mark"><ImageIcon size={22} /></div>
          <span>图仓</span>
        </div>
        <div className="login-copy">
          <h1 id="login-title">进入图片库</h1>
          <p>上传入口仅对管理员开放，图片直链可公开访问。</p>
        </div>
        <form onSubmit={submit} className="login-form">
          <label htmlFor="password">管理密码</label>
          <div className="password-field">
            <LockKeyhole size={18} aria-hidden="true" />
            <input
              id="password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="输入密码"
              required
              autoFocus
            />
          </div>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="primary-button full-button" type="submit" disabled={submitting}>
            {submitting ? <LoaderCircle className="spin" size={18} /> : <LockKeyhole size={18} />}
            登录
          </button>
        </form>
      </section>
    </main>
  );
}

function Library({ onLogout }) {
  const [images, setImages] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState("");
  const [uploads, setUploads] = useState([]);
  const [toast, setToast] = useState(null);
  const [preview, setPreview] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const fileInput = useRef(null);

  const notify = useCallback((message, tone = "default") => {
    setToast({ id: Date.now(), message, tone });
  }, []);

  const loadImages = useCallback(async ({ append = false, nextCursor = null } = {}) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "60" });
      if (nextCursor) params.set("cursor", nextCursor);
      const data = await api(`/api/images?${params}`);
      setImages((current) => append ? [...current, ...data.images] : data.images);
      setCursor(data.cursor);
    } catch (error) {
      if (error.status === 401) return onLogout();
      notify(error.message, "error");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [notify, onLogout]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const visibleImages = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) return images;
    return images.filter((image) => image.name.toLocaleLowerCase().includes(normalized));
  }, [images, query]);

  async function logout() {
    try {
      await api("/api/logout", { method: "POST" });
    } finally {
      onLogout();
    }
  }

  async function acceptFiles(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const valid = [];
    for (const file of files) {
      if (!ACCEPTED_TYPES.includes(file.type)) {
        notify(`${file.name} 的格式不受支持`, "error");
      } else if (file.size > MAX_FILE_SIZE) {
        notify(`${file.name} 超过 10 MB`, "error");
      } else {
        valid.push(file);
      }
    }

    const queue = valid.map((file) => ({
      id: crypto.randomUUID(),
      name: file.name,
      status: "uploading",
    }));
    setUploads((current) => [...queue, ...current]);

    let completed = 0;
    for (let index = 0; index < valid.length; index += 1) {
      const file = valid[index];
      const item = queue[index];
      const form = new FormData();
      form.append("file", file);
      try {
        const data = await api("/api/images", { method: "POST", body: form });
        setImages((current) => [data.image, ...current]);
        setUploads((current) => current.map((upload) => upload.id === item.id
          ? { ...upload, status: "done" }
          : upload));
        completed += 1;
      } catch (error) {
        setUploads((current) => current.map((upload) => upload.id === item.id
          ? { ...upload, status: "error", error: error.message }
          : upload));
      }
    }

    if (completed) notify(`已上传 ${completed} 张图片`, "success");
    window.setTimeout(() => {
      setUploads((current) => current.filter((upload) => upload.status !== "done"));
    }, 2400);
  }

  async function removeImage(image) {
    try {
      await api(`/api/images/${encodeURIComponent(image.key)}`, { method: "DELETE" });
      setImages((current) => current.filter((item) => item.key !== image.key));
      setDeleting(null);
      setPreview((current) => current?.key === image.key ? null : current);
      notify("图片已删除", "success");
    } catch (error) {
      notify(error.message, "error");
    }
  }

  async function copyValue(value, message) {
    try {
      await navigator.clipboard.writeText(value);
      notify(message, "success");
    } catch {
      notify("复制失败，请手动复制", "error");
    }
  }

  function absoluteUrl(image) {
    return new URL(image.url, window.location.origin).toString();
  }

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand-row compact">
            <div className="brand-mark"><ImageIcon size={20} /></div>
            <span>图仓</span>
          </div>
          <div className="topbar-actions">
            <span className="storage-badge"><span />R2 私有上传</span>
            <IconButton label="退出登录" onClick={logout}><LogOut size={19} /></IconButton>
          </div>
        </div>
      </header>

      <main className="library-page">
        <section className="page-heading">
          <div>
            <p className="eyebrow">图片库</p>
            <h1>管理图片</h1>
          </div>
          <button className="primary-button" type="button" onClick={() => fileInput.current?.click()}>
            <Upload size={18} />
            上传图片
          </button>
        </section>

        <UploadZone onFiles={acceptFiles} inputRef={fileInput} />

        <section className="library-toolbar" aria-label="图片工具栏">
          <div className="search-field">
            <Search size={18} aria-hidden="true" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索文件名"
              aria-label="搜索文件名"
            />
            {query ? (
              <button type="button" onClick={() => setQuery("")} aria-label="清空搜索"><X size={16} /></button>
            ) : null}
          </div>
          <div className="toolbar-meta">
            <span>{visibleImages.length} 张</span>
            <IconButton label="刷新图片列表" onClick={() => loadImages()} disabled={loading}>
              <RefreshCw className={loading ? "spin" : ""} size={18} />
            </IconButton>
          </div>
        </section>

        {loading ? (
          <GridLoading />
        ) : visibleImages.length ? (
          <section className="image-grid" aria-label="图片列表">
            {visibleImages.map((image) => (
              <ImageCard
                key={image.key}
                image={image}
                onPreview={() => setPreview(image)}
                onCopy={() => copyValue(absoluteUrl(image), "直链已复制")}
                onMarkdown={() => copyValue(`![${escapeMarkdown(image.name)}](${absoluteUrl(image)})`, "Markdown 已复制")}
                onDelete={() => setDeleting(image)}
              />
            ))}
          </section>
        ) : (
          <EmptyState query={query} onUpload={() => fileInput.current?.click()} />
        )}

        {cursor && !query ? (
          <div className="load-more-row">
            <button className="secondary-button" type="button" disabled={loadingMore} onClick={() => loadImages({ append: true, nextCursor: cursor })}>
              {loadingMore ? <LoaderCircle className="spin" size={18} /> : <ChevronDown size={18} />}
              加载更多
            </button>
          </div>
        ) : null}
      </main>

      {uploads.length ? <UploadStatus uploads={uploads} /> : null}
      {toast ? <Toast key={toast.id} toast={toast} onClose={() => setToast(null)} /> : null}
      {preview ? (
        <PreviewDialog
          image={preview}
          absoluteUrl={absoluteUrl(preview)}
          onClose={() => setPreview(null)}
          onCopy={() => copyValue(absoluteUrl(preview), "直链已复制")}
        />
      ) : null}
      {deleting ? (
        <ConfirmDialog
          image={deleting}
          onCancel={() => setDeleting(null)}
          onConfirm={() => removeImage(deleting)}
        />
      ) : null}
    </div>
  );
}

function UploadZone({ onFiles, inputRef }) {
  const [dragging, setDragging] = useState(false);

  function handleDrop(event) {
    event.preventDefault();
    setDragging(false);
    onFiles(event.dataTransfer.files);
  }

  return (
    <section
      className={`upload-zone${dragging ? " is-dragging" : ""}`}
      onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
      onDragOver={(event) => event.preventDefault()}
      onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false); }}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") inputRef.current?.click(); }}
      aria-label="选择或拖入图片"
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        multiple
        hidden
        onChange={(event) => { onFiles(event.target.files); event.target.value = ""; }}
      />
      <div className="upload-icon"><CloudUpload size={25} /></div>
      <div>
        <strong>拖入图片，或点击选择</strong>
        <p>JPG、PNG、GIF、WebP、AVIF、ICO，单张不超过 10 MB</p>
      </div>
    </section>
  );
}

function ImageCard({ image, onPreview, onCopy, onMarkdown, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    function close(event) {
      if (!menuRef.current?.contains(event.target)) setMenuOpen(false);
    }
    document.addEventListener("pointerdown", close);
    return () => document.removeEventListener("pointerdown", close);
  }, [menuOpen]);

  return (
    <article className="image-card">
      <button className="thumbnail-button" type="button" onClick={onPreview} aria-label={`预览 ${image.name}`}>
        <img src={image.url} alt={image.name} loading="lazy" />
        <span className="preview-hint"><Eye size={17} />预览</span>
      </button>
      <div className="image-card-body">
        <div className="image-info">
          <strong title={image.name}>{image.name}</strong>
          <span>{formatBytes(image.size)} · {formatDate(image.uploadedAt)}</span>
        </div>
        <div className="card-actions">
          <IconButton label="复制图片直链" onClick={onCopy}><Copy size={17} /></IconButton>
          <div className="menu-wrap" ref={menuRef}>
            <IconButton label="更多操作" onClick={() => setMenuOpen((open) => !open)} ariaExpanded={menuOpen}>
              <MoreHorizontal size={18} />
            </IconButton>
            {menuOpen ? (
              <div className="action-menu">
                <button type="button" onClick={() => { onMarkdown(); setMenuOpen(false); }}><Code2 size={16} />复制 Markdown</button>
                <button className="danger-action" type="button" onClick={() => { onDelete(); setMenuOpen(false); }}><Trash2 size={16} />删除图片</button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
}

function PreviewDialog({ image, absoluteUrl, onClose, onCopy }) {
  useEffect(() => {
    function close(event) { if (event.key === "Escape") onClose(); }
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [onClose]);

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="preview-dialog" role="dialog" aria-modal="true" aria-labelledby="preview-name">
        <header>
          <div>
            <strong id="preview-name">{image.name}</strong>
            <span>{formatBytes(image.size)}</span>
          </div>
          <IconButton label="关闭预览" onClick={onClose}><X size={20} /></IconButton>
        </header>
        <div className="preview-canvas"><img src={image.url} alt={image.name} /></div>
        <footer>
          <div className="link-field"><span>{absoluteUrl}</span></div>
          <button className="primary-button" type="button" onClick={onCopy}><Clipboard size={18} />复制直链</button>
        </footer>
      </section>
    </div>
  );
}

function ConfirmDialog({ image, onCancel, onConfirm }) {
  const [working, setWorking] = useState(false);
  return (
    <div className="dialog-backdrop" role="presentation">
      <section className="confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="delete-title">
        <div className="danger-icon"><Trash2 size={21} /></div>
        <div>
          <h2 id="delete-title">删除这张图片？</h2>
          <p><strong>{image.name}</strong> 的公开直链会立即失效，此操作无法撤销。</p>
        </div>
        <div className="confirm-actions">
          <button className="secondary-button" type="button" onClick={onCancel} disabled={working}>取消</button>
          <button className="danger-button" type="button" disabled={working} onClick={async () => { setWorking(true); await onConfirm(); setWorking(false); }}>
            {working ? <LoaderCircle className="spin" size={18} /> : <Trash2 size={18} />}
            删除
          </button>
        </div>
      </section>
    </div>
  );
}

function EmptyState({ query, onUpload }) {
  return (
    <section className="empty-state">
      <div className="empty-icon"><ImageIcon size={24} /></div>
      <h2>{query ? "没有匹配的图片" : "图片库还是空的"}</h2>
      <p>{query ? "换一个文件名试试。" : "上传第一张图片后，直链会显示在这里。"}</p>
      {!query ? <button className="secondary-button" type="button" onClick={onUpload}><Upload size={18} />选择图片</button> : null}
    </section>
  );
}

function GridLoading() {
  return <section className="image-grid" aria-label="正在加载图片">{Array.from({ length: 8 }, (_, index) => <div className="image-skeleton" key={index} />)}</section>;
}

function UploadStatus({ uploads }) {
  return (
    <aside className="upload-status" aria-live="polite">
      <strong>上传队列</strong>
      {uploads.slice(0, 4).map((upload) => (
        <div key={upload.id} className={`upload-row ${upload.status}`}>
          {upload.status === "uploading" ? <LoaderCircle className="spin" size={16} /> : upload.status === "done" ? <Check size={16} /> : <X size={16} />}
          <span title={upload.error || upload.name}>{upload.name}</span>
        </div>
      ))}
    </aside>
  );
}

function Toast({ toast, onClose }) {
  useEffect(() => {
    const timer = window.setTimeout(onClose, 3200);
    return () => window.clearTimeout(timer);
  }, [onClose]);
  return (
    <div className={`toast ${toast.tone}`} role="status">
      {toast.tone === "success" ? <Check size={17} /> : toast.tone === "error" ? <X size={17} /> : <ImageIcon size={17} />}
      <span>{toast.message}</span>
      <button type="button" onClick={onClose} aria-label="关闭提示"><X size={15} /></button>
    </div>
  );
}

function IconButton({ label, children, onClick, disabled = false, ariaExpanded }) {
  return (
    <button
      className="icon-button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-expanded={ariaExpanded}
    >
      {children}
    </button>
  );
}

async function api(url, options = {}) {
  const response = await fetch(url, { credentials: "same-origin", ...options });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || "请求失败");
    error.status = response.status;
    throw error;
  }
  return data;
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes)) return "未知大小";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatDate(value) {
  if (!value) return "未知时间";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "未知时间";
  return new Intl.DateTimeFormat("zh-CN", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }).format(date);
}

function escapeMarkdown(value) {
  return value.replace(/[\[\]\\]/g, "\\$&");
}

createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);
